import {
  AfterViewInit,
  Component,
  Directive,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { ActiveDescendantKeyManager, Highlightable } from '@angular/cdk/a11y'
import { debounceTime, filter, first, switchMap, takeUntil, tap } from 'rxjs/operators'
import { fromEvent, Subject } from 'rxjs'

import { ToolbarService } from '@app/core/modules/toolbar/toolbar.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { User } from '@shared/models/user.model'
import { TeamType } from '@shared/models/team.model'
import { Vehicle } from '@shared/models/vehicle.model'
import { RoleType } from '@core/types/role.type'

import { ToolbarSearchResultsTriggerDirective } from './toolbar-search-results-trigger.directive'
import { SearchResult, SearchResultType } from './search-result.model'
import { DialogService } from '@shared/dialogs/dialog.service'
import { TranslateService } from '@ngx-translate/core'
import { VehiclesService } from '@app/modules/client/vehicles/vehicles.service'
import { ClientsService } from '@app/modules/admin/clients/clients.service'
import { DestroyService } from '@shared/services/destroy.service'

@Directive({
  selector: '[linToolbarSearchResultRow]',
  standalone: false,
})
export class ToolbarSearchResultRowDirective implements Highlightable, OnDestroy {
  @HostBinding('class.result-row_active') active = false

  inactive$ = new Subject<void>()

  constructor(
    private host: ElementRef<HTMLElement>,
    public authService: AuthService,
  ) {}

  setActiveStyles() {
    this.active = true
    this.host.nativeElement.scrollIntoView({ block: 'center' })

    fromEvent(document, 'keydown')
      .pipe(
        filter((event: KeyboardEvent) => event.key === 'Enter'),
        takeUntil(this.inactive$),
      )
      .subscribe(() => this.host.nativeElement.click())
  }

  setInactiveStyles() {
    this.active = false
    this.inactive$.next()
  }

  ngOnDestroy() {
    this.inactive$.next()
    this.inactive$.complete()
  }
}

@Component({
  selector: 'lin-toolbar-search',
  templateUrl: './toolbar-search.component.html',
  styleUrls: [`./toolbar-search.component.scss`],
  providers: [DestroyService],
  standalone: false,
})
export class ToolbarSearchComponent implements OnInit, AfterViewInit {
  input = new FormControl()
  isLoading = false
  searchResult: SearchResult = null
  user: User
  keyManager: ActiveDescendantKeyManager<ToolbarSearchResultRowDirective>
  toggleState = false

  SearchResultType = SearchResultType

  get isSuperAdmin(): boolean {
    return this.authService.getCurrentUser().role.name === RoleType.SuperAdmin
  }

  @ViewChild(ToolbarSearchResultsTriggerDirective, { static: true }) private readonly searchTrigger: ToolbarSearchResultsTriggerDirective
  @ViewChildren(ToolbarSearchResultRowDirective) private readonly resultRows: QueryList<ToolbarSearchResultRowDirective>

  constructor(
    private authService: AuthService,
    private toolbarService: ToolbarService,
    private readonly dialog: DialogService,
    private readonly translateService: TranslateService,
    private readonly vehiclesService: VehiclesService,
    private readonly clientsService: ClientsService,
    private readonly destroyService: DestroyService,
  ) {}

  ngOnInit(): void {
    this.authService.currentUser$.pipe(first()).subscribe((user) => (this.user = user))

    this.handleInputChanges()
  }

  ngAfterViewInit(): void {
    this.keyManager = new ActiveDescendantKeyManager<ToolbarSearchResultRowDirective>(this.resultRows).withWrap()
    fromEvent(document, 'keydown')
      .pipe(takeUntil(this.destroyService))
      .subscribe((event: KeyboardEvent) => this.keyManager.onKeydown(event))
  }

  handleInputChanges(): void {
    this.input.valueChanges
      .pipe(
        tap(() => {
          this.isLoading = false
          this.searchResult = null
        }),
        filter((term: string) => term.length >= 3),
        tap(() => (this.isLoading = true)),
        debounceTime(300),
        switchMap((term) => this.toolbarService.search(term)),
        takeUntil(this.destroyService),
      )
      .subscribe((result) => {
        this.searchResult = result
        result.forEach((type) => {
          if (!this.isSuperAdmin) {
            type.type === 'vehicle' && (type.items = type.items.filter((item) => item.status !== 'deleted'))
            type.type === 'user' && (type.items = type.items.filter((item) => item.status !== 'deleted'))
          }
        })

        this.isLoading = false
        this.resetKeyManager()
        this.searchTrigger.openDropDown()
      })
  }

  resetKeyManager(): void {
    this.keyManager.setActiveItem(-1)
  }

  getClientUrl(clientId: number): string {
    return `/admin/clients/${clientId}/company-info`
  }

  getUserUrl(user: User): string {
    return user.team.type === TeamType.Client ? `/admin/clients/${user.team.clientId}/users/${user.id}` : `/admin/team/users/${user.id}`
  }

  getDeviceUrl(deviceId: number): string {
    return `/admin/devices/${deviceId}`
  }

  getVehicleUrl(vehicle: Vehicle): string {
    return vehicle.deviceId ? `/admin/devices/${vehicle.deviceId}/vehicle` : `/client/fleet/${vehicle.id}/status`
  }

  onVehicleClick(event: MouseEvent, vehicle: Vehicle): void {
    event.preventDefault()

    const url = this.getVehicleUrl(vehicle)
    vehicle.deviceId ? window.open(url, '_blank') : this.authService.impersonateAsClient(vehicle.team.clientId, url)
  }

  getGeofenceUrl(): string {
    return '/client/configuration/geofences'
  }

  getToggleIcon(): string {
    return this.toggleState ? 'search_off' : 'search'
  }

  toggleSearch(): void {
    this.toggleState = !this.toggleState
  }

  onGeofenceClick(event: MouseEvent, clientId: number): void {
    event.preventDefault()
    this.authService.impersonateAsClient(clientId, this.getGeofenceUrl())
  }

  undeleteVehicle(vehicleId: number): void {
    this.dialog
      .confirm(<string>this.translateService.instant('DIALOGS.MESSAGES.UNDELETE_VEHICLE'))
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.vehiclesService.undeleteVehicle(vehicleId)),
        takeUntil(this.destroyService),
      )
      .subscribe(() => this.input.setValue(this.input.value))
  }

  undeleteUser(userId: number): void {
    this.dialog
      .confirm(<string>this.translateService.instant('DIALOGS.MESSAGES.UNDELETE_USER'))
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.clientsService.undeleteUser(userId)),
        takeUntil(this.destroyService),
      )
      .subscribe(() => this.input.setValue(this.input.value))
  }
}
